import React, {useMemo} from 'react';

import {Button, Select} from '@kit';

import {humanEnumeration} from '../../../../../components/utils';
import {useCharacter} from '../../character-context';
import {COIN_SHORT_TITLES} from '../../model/coins';
import type {Coin} from '../../model/character-state';
import {
	applyStartingEquipment,
	parseStartingEquipment,
	pickOptions,
	proficientPick,
	resolveStartingOption,
	StartingEntry,
	StartingItemInfo,
	StartingOption,
	StartingSource,
} from '../../model/starting-equipment';
import {PickList} from '../pick-list';

import css from '../master.module.css';

type Props = {
	source: StartingSource;
	title: string;
	/** текст снаряжения класса или предыстории */
	text?: string;
	items: StartingItemInfo[];
	titles: Map<string, string>;
};

/** выбор варианта стартового снаряжения; выбранный вариант сразу попадает в лист */
export const StartingEquipmentChoice: React.FC<Props> = ({source, title, text, items, titles}) => {
	const {state, character, update} = useCharacter();
	const options = useMemo(() => parseStartingEquipment(text), [text]);
	const selection = state.startingEquipment?.[source];
	const selected = options.find((option) => option.key === selection?.option);
	const proficiencies = [...character.toolProficiencies, ...character.instrumentProficiencies];

	if (!options.length) return null;

	const apply = (option: StartingOption | undefined, picks: Record<string, string>) =>
		update((current) =>
			applyStartingEquipment(
				current,
				source,
				option && {option: option.key, picks},
				option && resolveStartingOption(option, picks, items),
			),
		);

	const selectOption = (key: string) => {
		const option = options.find((candidate) => candidate.key === key);
		if (!option) return;
		const picks: Record<string, string> = {};
		for (const entry of option.entries) {
			if (entry.kind !== 'pick') continue;
			const itemId = proficientPick(entry, items, proficiencies);
			if (itemId) picks[entry.key] = itemId;
		}
		apply(option, picks);
	};

	const setPick = (key: string, itemId: string | undefined) => {
		if (!selected || !selection) return;
		const {[key]: _previous, ...picks} = selection.picks;
		apply(selected, itemId ? {...picks, [key]: itemId} : picks);
	};

	return (
		<section className={css.startingEquipment}>
			<h3 className={css.startingTitle}>{title}</h3>
			<PickList
				items={options.map((option) => ({
					id: option.key,
					title: `Вариант ${option.key}`,
					caption: optionCaption(option, titles),
				}))}
				selectedId={selection?.option}
				onSelect={selectOption}
			/>
			{selected?.entries.map((entry) =>
				entry.kind === 'pick' ? (
					<PickField
						key={entry.key}
						entry={entry}
						items={items}
						value={selection?.picks[entry.key]}
						onChange={(itemId) => setPick(entry.key, itemId)}
					/>
				) : null,
			)}
			{selection && (
				<Button appearance="subtle" size="small" onClick={() => apply(undefined, {})}>
					Не брать снаряжение
				</Button>
			)}
		</section>
	);
};

type PickFieldProps = {
	entry: Extract<StartingEntry, {kind: 'pick'}>;
	items: StartingItemInfo[];
	value?: string;
	onChange(itemId: string | undefined): void;
};

const PickField: React.FC<PickFieldProps> = ({entry, items, value, onChange}) => (
	<Select
		label={labelText(entry.label)}
		items={pickOptions(entry, items).map((item) => ({value: item.id, label: item.title}))}
		value={value ?? null}
		placeholder="Выберите…"
		onChange={([itemId]) => onChange(itemId === undefined ? undefined : String(itemId))}
	/>
);

const labelText = (label: string) => label.replace(/\{@item\s+[^,}]+,\s*([^}]*)\}/g, '$1');

function optionCaption(option: StartingOption, titles: Map<string, string>): string {
	const entries = option.entries.map((entry) => {
		if (entry.kind === 'item') {
			const title = titles.get(entry.itemId) || entry.itemId;
			return entry.quantity > 1 ? `${title} × ${entry.quantity}` : title;
		}
		return entry.kind === 'pick' ? labelText(entry.label) : entry.text;
	});
	const coins = (Object.entries(option.coins) as [Coin, number][]).map(
		([coin, amount]) => `${amount} ${COIN_SHORT_TITLES[coin]}`,
	);
	return humanEnumeration([...entries, ...coins]);
}
